import { useState, useEffect } from "react"
import { Send, Plus } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { Label } from "../ui/label"
import { Textarea } from "../ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select"

interface TeamSuggestionData {
  name: string
  city: string
  state: string
  league: string
  sport: string
  reason: string
}

interface TeamSuggestionDialogProps {
  isOpen: boolean
  onClose: () => void
}

export function TeamSuggestionDialog({ isOpen, onClose }: TeamSuggestionDialogProps) {
  // Use the provided isOpen prop instead of internal state
  const [open, setOpen] = useState(isOpen)
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState<TeamSuggestionData>({
    name: "",
    city: "",
    state: "",
    league: "",
    sport: "",
    reason: ""
  })

  const sports = [
    "Football",
    "Basketball", 
    "Baseball",
    "Hockey",
    "Soccer",
    "Other"
  ]

  const leagues = [
    "NFL",
    "NBA", 
    "MLB",
    "NHL",
    "MLS",
    "Premier League",
    "La Liga",
    "NCAA",
    "Minor League",
    "Other"
  ]

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Submit to the API endpoint
      const response = await fetch('/api/suggest-team', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      })
      
      if (!response.ok) {
        throw new Error('Failed to submit suggestion')
      }
      
      // Reset form and close dialog
      setFormData({
        name: "",
        city: "",
        state: "",
        league: "",
        sport: "",
        reason: ""
      })
      setOpen(false)
      
      alert("Thank you for your suggestion! We'll review it and add the team if appropriate.")
      
    } catch (error) {
      console.error("Error submitting suggestion:", error)
      alert("Sorry, there was an error submitting your suggestion. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const updateFormData = (field: keyof TeamSuggestionData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  // Update useEffect to sync with external isOpen state
  useEffect(() => {
    setOpen(isOpen)
  }, [isOpen])
  
  return (
    <Dialog 
      open={open} 
      onOpenChange={(newOpen) => {
        setOpen(newOpen)
        if (!newOpen) onClose()
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full">
          <Plus className="h-4 w-4 mr-2" />
          Don't see your team? Let us know!
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle>Suggest a Team</DialogTitle>
          <DialogDescription>
            Can't find a team you're looking for? Tell us about it and we'll consider adding it to our database.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Team Name *</Label>
              <Input
                id="name"
                placeholder="e.g. Lakers"
                value={formData.name}
                onChange={(e) => updateFormData("name", e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="city">City *</Label>
              <Input
                id="city"
                placeholder="e.g. Los Angeles"
                value={formData.city}
                onChange={(e) => updateFormData("city", e.target.value)}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="state">State/Province</Label>
              <Input
                id="state"
                placeholder="e.g. CA"
                value={formData.state}
                onChange={(e) => updateFormData("state", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sport">Sport *</Label>
              <Select onValueChange={(value) => updateFormData("sport", value)} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select sport" />
                </SelectTrigger>
                <SelectContent>
                  {sports.map((sport) => (
                    <SelectItem key={sport} value={sport}>
                      {sport}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="league">League/Division *</Label>
            <Select onValueChange={(value) => updateFormData("league", value)} required>
              <SelectTrigger>
                <SelectValue placeholder="Select league" />
              </SelectTrigger>
              <SelectContent>
                {leagues.map((league) => (
                  <SelectItem key={league} value={league}>
                    {league}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Why should we add this team?</Label>
            <Textarea
              id="reason"
              placeholder="Tell us why this team would be a great addition..."
              value={formData.reason}
              onChange={(e) => updateFormData("reason", e.target.value)}
              rows={3}
            />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                "Sending..."
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Suggestion
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}